import { useSavedSearch } from 'components/SavedSearch/SavedSearchContextProvider';
import {
  getQuickFilterIdQp,
  normalizeQpStr,
  QUICKFILTER_ID_QP,
  SavedSearchSelector,
} from 'components/SavedSearch/SavedSearchSelector';
import { buildSavedSearchParams } from 'components/Search/functions';
import {
  GetSavedSearchByIdResult,
  SavedSearchItem,
} from 'providers/contexts/extensions/SavedSearches';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDataProvider } from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import { NavigateOptions, useSearchParams } from 'react-router-dom';
import { useWireSearch } from 'screens/syndication/views/search/syndication/WireSearchWithContext';
import { ApiErrorResponse, BloxCustomDataProviderType } from 'utility/types/dataProvider';

// keep quickfilter value if just sort/dir changes and ignore loader param
const keysIgnore = ['sort', 'dir', QUICKFILTER_ID_QP];

export const WireQuickFilterSelector = memo(() => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const { handleQuickFitlerChange, selectedChannel } = useWireSearch();
  const {
    setSavedSearchOpen,
    quickFilterValue,
    updateQuickFilterValue,
    lastQuickFilterRef,
  } = useSavedSearch();
  const [searchParams, setSearchParams] = useSearchParams();

  // extract possible quick filter fetch id from the qp
  const maybeQuickFilterId = getQuickFilterIdQp(searchParams);

  const {
    data: getIdData,
    isLoading: getIdIsLoading,
    isError: getIdIsError,
    isSuccess: getIdIsSuccess,
  } = useQuery<GetSavedSearchByIdResult, ApiErrorResponse, GetSavedSearchByIdResult>({
    queryKey: ['getSavedSearchById', maybeQuickFilterId],
    queryFn: async () => {
      if (!maybeQuickFilterId) {
        return;
      }
      const result = await dataProvider.getSavedSearchById({
        path: { id: maybeQuickFilterId },
      });
      return result;
    },
    enabled: Boolean(maybeQuickFilterId),
    gcTime: 0, // no auto bg refetch / no cache
    staleTime: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Disabled and loading states for selector btn
  const isDisabled = getIdIsLoading;
  const isLoading = getIdIsLoading;

  // track last pending/applied params
  const pendingChangeRef = useRef(false);

  // handle applying the filter
  const applyFilter = useCallback(
    async (item: SavedSearchItem, navOpts: NavigateOptions) => {
      const applied = await handleQuickFitlerChange(item);
      // channel changed while the config fetch was in-flight, do not
      // overwrite the URL with this now stale filter
      if (!applied) {
        return;
      }
      // build editorial saved search filter payload
      const ssPayload = item?.payload || {};
      const newParamStr = buildSavedSearchParams({
        payload: ssPayload,
      });
      // if empty do not apply
      if (!newParamStr) {
        return;
      }
      // preserve the active channel if the saved search payload has none,
      // otherwise URL sync would drop channel_id and fall back to the first channel
      const savedSearchParams = new URLSearchParams(newParamStr);
      const savedSearchChannel = ssPayload.channel_id || selectedChannel;
      if (savedSearchChannel && !savedSearchParams.has('channel_id')) {
        savedSearchParams.set('channel_id', savedSearchChannel);
      }
      // apply to select state, url, and track pending/applied via refs
      pendingChangeRef.current = true;
      updateQuickFilterValue(item);
      setSearchParams(savedSearchParams, navOpts);
    },
    [setSearchParams, updateQuickFilterValue, handleQuickFitlerChange, selectedChannel],
  );

  useEffect(() => {
    if (!maybeQuickFilterId) {
      return;
    }
    if (getIdIsLoading) {
      return;
    }

    // upon fetched set the value and remove param
    // apply the filter to both state and URL, replace history stack
    // as we are removing the quickFilterId param as well...
    if (getIdIsSuccess && getIdData?.data) {
      applyFilter(getIdData.data, { replace: true });
    }

    // If the fetch errored, reset the filter value
    // and just remove the quickFilterId param
    if (getIdIsError) {
      updateQuickFilterValue(null);

      // remove just the quickFilterId param as fetch failed
      setSearchParams(
        (prevParams) => {
          const newParams = new URLSearchParams(prevParams.toString());
          newParams.delete(QUICKFILTER_ID_QP);
          return newParams;
        },
        { replace: true },
      );
    }
  }, [
    applyFilter,
    getIdData?.data,
    getIdIsLoading,
    getIdIsSuccess,
    getIdIsError,
    maybeQuickFilterId,
    setSearchParams,
    updateQuickFilterValue,
  ]);

  // add in client side items to the selector, in this case
  // adding to the list the selected filter value, and
  // selector internally should deduplicate items by id anyway
  const addItems = useMemo(() => {
    return quickFilterValue ? [quickFilterValue] : [];
  }, [quickFilterValue]);

  // dispatch filter apply on item select
  const onFilterSelect = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, item: SavedSearchItem) => {
      applyFilter(item, { replace: false });
    },
    [applyFilter],
  );

  // reset quick filter value if the query params change and
  // does not match the last applied params as normalized
  useEffect(() => {
    // prevent instant reset upon filter selection
    if (pendingChangeRef.current) {
      pendingChangeRef.current = false;
      return;
    }

    // no value or no last applied params ref
    if (!quickFilterValue || !lastQuickFilterRef.current) {
      return;
    }

    // compare current vs last applied params
    const normalizedCurrent = normalizeQpStr({
      qpstr: searchParams.toString(),
      ignoreKeys: keysIgnore,
    });

    const lastQpStr = buildSavedSearchParams({
      payload: lastQuickFilterRef?.current?.payload ?? {},
    });

    const normalizedLast = normalizeQpStr({
      qpstr: lastQpStr,
      ignoreKeys: keysIgnore,
    });

    // mismatch indicates params changed so
    // reset value as that filter may not be applied anymore
    if (normalizedCurrent !== normalizedLast) {
      updateQuickFilterValue(null);
    }
  }, [lastQuickFilterRef, quickFilterValue, searchParams, updateQuickFilterValue]);

  // open saved search modal on new click
  const createNewSavedSearch = useCallback(() => {
    setSavedSearchOpen(true);
  }, [setSavedSearchOpen]);

  return (
    <SavedSearchSelector
      value={quickFilterValue}
      clientItems={addItems}
      disabled={isDisabled}
      loading={isLoading}
      onSelectCb={onFilterSelect}
      createNewCb={createNewSavedSearch}
      addPayload={{
        query: {
          app: ['wire'],
        },
      }}
    />
  );
});
WireQuickFilterSelector.displayName = 'WireQuickFilterSelector';
